import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { modsAPI, vehiclesAPI, enumsAPI, customCategoriesAPI, subscriptionAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowLeft, Loader2, Save, Trash2, Package, CalendarIcon, Plus, Crown } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function ModForm() {
  const { id, vehicleId, modId } = useParams();
  const actualVehicleId = id || vehicleId;
  const navigate = useNavigate();
  const isEdit = Boolean(modId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [categories, setCategories] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [dateOpen, setDateOpen] = useState(false);
  const [isPro, setIsPro] = useState(false);
  
  // Custom category dialog
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);

  const [form, setForm] = useState({
    vehicle_id: actualVehicleId,
    category: "",
    item_name: "",
    brand: "",
    part_number: "",
    install_date: "",
    install_mileage: "",
    cost: "",
    vendor: "",
    vendor_link: "",
    notes: "",
    tags: [],
  });

  useEffect(() => {
    loadData();
  }, [actualVehicleId, modId]);

  const loadData = async () => {
    try {
      const [vehicleRes, categoriesRes, tagsRes, subRes] = await Promise.all([
        vehiclesAPI.get(actualVehicleId),
        enumsAPI.modCategories(),
        enumsAPI.modTags(),
        subscriptionAPI.status(),
      ]);
      setVehicle(vehicleRes.data);
      setCategories(categoriesRes.data);
      setAvailableTags(tagsRes.data);
      setIsPro(subRes.data.is_pro);

      if (isEdit) {
        const modRes = await modsAPI.get(modId);
        const m = modRes.data;
        setForm({
          vehicle_id: actualVehicleId,
          category: m.category || "",
          item_name: m.item_name || "",
          brand: m.brand || "",
          part_number: m.part_number || "",
          install_date: m.install_date?.split("T")[0] || "",
          install_mileage: m.install_mileage || "",
          cost: m.cost || "",
          vendor: m.vendor || "",
          vendor_link: m.vendor_link || "",
          notes: m.notes || "",
          tags: m.tags || [],
        });
      }
    } catch (error) {
      toast.error("Failed to load data");
      navigate(`/vehicles/${actualVehicleId}/build`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category) {
      toast.error("Category is required");
      return;
    }
    if (!form.item_name.trim()) {
      toast.error("Item name is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        install_mileage: form.install_mileage ? parseInt(form.install_mileage) : null,
        cost: form.cost ? parseFloat(form.cost) : 0,
        install_date: form.install_date || null,
      };

      if (isEdit) {
        await modsAPI.update(modId, payload);
        toast.success("Mod updated");
      } else {
        await modsAPI.create(payload);
        toast.success("Mod added");
      }
      navigate(`/vehicles/${actualVehicleId}/build`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save mod");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await modsAPI.delete(modId);
      toast.success("Mod deleted");
      navigate(`/vehicles/${actualVehicleId}/build`);
    } catch (error) {
      toast.error("Failed to delete mod");
    } finally {
      setDeleting(false);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleTag = (tag) => {
    setForm((prev) => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter((t) => t !== tag)
        : [...prev.tags, tag],
    }));
  };

  const handleAddCategory = async () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) {
      toast.error("Category name is required");
      return;
    }
    setAddingCategory(true);
    try {
      await customCategoriesAPI.create({ name: trimmed, type: "mod" });
      toast.success(`Category "${trimmed}" added`);
      // Refresh categories list
      const categoriesRes = await enumsAPI.modCategories();
      setCategories(categoriesRes.data);
      // Auto-select the newly created category
      updateField("category", trimmed);
      setNewCategoryName("");
      setAddCategoryOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add category");
    } finally {
      setAddingCategory(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link
              to={`/vehicles/${actualVehicleId}/build`}
              data-testid="back-btn"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <HeaderIcon />

              <div>
                <h1 className="text-lg font-bold text-foreground">
                  {isEdit ? "Edit Mod" : "Add Mod"}
                </h1>
                <p className="text-xs text-muted-foreground">{vehicle?.nickname}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Mod Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>
                    Category <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex gap-2">
                    <Select
                      value={form.category}
                      onValueChange={(v) => updateField("category", v)}
                    >
                      <SelectTrigger
                        data-testid="mod-category-select"
                        className="bg-zinc-900 border-zinc-800 flex-1"
                      >
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800">
                        {categories.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isPro && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        data-testid="add-custom-category-btn"
                        className="border-zinc-800 shrink-0"
                        onClick={() => setAddCategoryOpen(true)}
                        title="Add custom category"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {!isPro && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Crown className="w-3 h-3 text-amber-500" />
                      Pro users can add custom categories
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="item_name">
                    Item Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="item_name"
                    data-testid="mod-name-input"
                    value={form.item_name}
                    onChange={(e) => updateField("item_name", e.target.value)}
                    placeholder="e.g., King 2.5 Coilovers"
                    className="bg-zinc-900 border-zinc-800"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="brand">Brand</Label>
                  <Input
                    id="brand"
                    data-testid="mod-brand-input"
                    value={form.brand}
                    onChange={(e) => updateField("brand", e.target.value)}
                    placeholder="e.g., King, Icon, ARB"
                    className="bg-zinc-900 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="part_number">Part Number</Label>
                  <Input
                    id="part_number"
                    data-testid="mod-part-number-input"
                    value={form.part_number}
                    onChange={(e) => updateField("part_number", e.target.value)}
                    placeholder="e.g., 25001-401"
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Install Info */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Install Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Install Date</Label>
                  <Popover open={dateOpen} onOpenChange={setDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="mod-date-btn"
                        className="w-full justify-start text-left font-mono bg-zinc-900 border-zinc-800"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.install_date
                          ? format(new Date(form.install_date + "T00:00:00"), "MMM dd, yyyy")
                          : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-zinc-950 border-zinc-800">
                      <Calendar
                        mode="single"
                        selected={form.install_date ? new Date(form.install_date + "T00:00:00") : undefined}
                        onSelect={(date) => {
                          if (date) {
                            // Use local date to avoid timezone shift
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            updateField("install_date", `${year}-${month}-${day}`);
                          }
                          setDateOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="install_mileage">Install Mileage</Label>
                  <Input
                    id="install_mileage"
                    data-testid="mod-mileage-input"
                    type="number"
                    value={form.install_mileage}
                    onChange={(e) => updateField("install_mileage", e.target.value)}
                    placeholder="Mileage at install"
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cost">Cost ($)</Label>
                  <Input
                    id="cost"
                    data-testid="mod-cost-input"
                    type="number"
                    step="0.01"
                    value={form.cost}
                    onChange={(e) => updateField("cost", e.target.value)}
                    placeholder="0.00"
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Vendor Info */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Vendor Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="vendor">Vendor</Label>
                  <Input
                    id="vendor"
                    data-testid="mod-vendor-input"
                    value={form.vendor}
                    onChange={(e) => updateField("vendor", e.target.value)}
                    placeholder="e.g., Trail Grid Pro, Amazon"
                    className="bg-zinc-900 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendor_link">Vendor Link</Label>
                  <Input
                    id="vendor_link"
                    data-testid="mod-vendor-link-input"
                    value={form.vendor_link}
                    onChange={(e) => updateField("vendor_link", e.target.value)}
                    placeholder="https://..."
                    className="bg-zinc-900 border-zinc-800"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tags & Notes */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Tags & Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex flex-wrap gap-3">
                  {availableTags.map((tag) => (
                    <div key={tag} className="flex items-center space-x-2">
                      <Checkbox
                        id={`tag-${tag}`}
                        data-testid={`tag-${tag}`}
                        checked={form.tags.includes(tag)}
                        onCheckedChange={() => toggleTag(tag)}
                      />
                      <label
                        htmlFor={`tag-${tag}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {tag}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  data-testid="mod-notes-input"
                  value={form.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder="Additional notes about this mod..."
                  className="bg-zinc-900 border-zinc-800 min-h-[100px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-between gap-4 pt-4">
            {isEdit && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="delete-mod-btn"
                    className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                    disabled={deleting}
                  >
                    {deleting ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-zinc-950 border-zinc-800">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Mod?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this mod from your build sheet. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-zinc-900 border-zinc-800">
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      data-testid="confirm-delete-mod-btn"
                      className="bg-red-500 hover:bg-red-600"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <div className="flex gap-3 ml-auto">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/vehicles/${actualVehicleId}/build`)}
                className="border-zinc-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="save-mod-btn"
                className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {isEdit ? "Save Changes" : "Add Mod"}
              </Button>
            </div>
          </div>
        </form>
      </main>

      {/* Add Custom Category Dialog */}
      <Dialog open={addCategoryOpen} onOpenChange={setAddCategoryOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Add Custom Category
            </DialogTitle>
            <DialogDescription>
              Create a new mod category for your build sheets.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <Label htmlFor="new-category-name">Category Name</Label>
            <Input
              id="new-category-name"
              data-testid="new-category-name-input"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="e.g., Communications, Recovery Gear"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddCategory();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCategoryOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-custom-category-btn"
              onClick={handleAddCategory}
              disabled={addingCategory || !newCategoryName.trim()}
            >
              {addingCategory && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
